/**
 * EL BORRADOR DEL CUADERNO (CQ4a): las DECISIONES DEL DOCUMENTO que no son hechos
 * de ninguna aplicación (D16, regla mixta), y la lista CERRADA de operaciones con
 * las que se cambian.
 *
 * Vive en `cuaderno_drafts` (migración 20260926140000), uno por titular y año
 * (D14), y SOLO se escribe con la RPC `apply_cuaderno_change` (y se crea con
 * `ensure_cuaderno_draft`). Este fichero es su espejo tipado y la ÚNICA fuente
 * de todo lo del borrador en el código: el contrato de cambios del asistente
 * (`cambios.ts`) lo importa de aquí y la edge lo lleva en su copia del motor
 * (scripts/sync-cuaderno.mjs):
 *   - `CuadernoOp`: las operaciones que acepta la RPC. El asistente (CQ4b) y la
 *     tarjeta de cambios las construyen con estos tipos; la RPC vuelve a validar
 *     TODO (lista blanca, alcance, fitosanitario registrado, recuento);
 *   - `validarOpBorrador`: la primera pasada de la RPC, la que no mira la base
 *     (lista blanca, tipos, motivo), con sus MISMOS motivos de rechazo;
 *   - `recuentoDeOps`: el `p_expected_count` que la RPC compara, contado igual
 *     (una por clave de set_answer y una por línea);
 *   - `leerBorrador`: del jsonb a las decisiones que entiende el motor;
 *   - `leerErrorCambio`: el error de la RPC, para enseñarlo sin inventar.
 *
 * Los hechos de la aplicación (máquina, aplicador, tiempo, eficacia de un parte)
 * NO van aquí: van al parte por la edge `update-spray-log-details` (F123), para
 * que el registro de /spray-logs y el cuaderno no se contradigan (A20).
 *
 * PURO: sin red ni imports de la app (solo `modeloOficial` y el normalizador de
 * nombres, puros los dos), para que el asistente lo pueda copiar.
 */
import type { DecisionesCuaderno, Eficacia } from "./entrada";
import { MACROORGANISMOS } from "./modeloOficial";
import { normalizeProductName } from "@/lib/productMatch";

/** Claves de `set_answer` con valor sí/no. La MISMA lista que `c_claves_bool` en la RPC. */
export const CLAVES_SI_NO = [
  "aplica_3_2",
  "aplica_3_3",
  "aplica_3_4",
  "aplica_3_5",
  "periodo_confirmado",
  "hubo_analisis",
  "eficacia_confirmada",
] as const;

/** Todas las claves que acepta `set_answer` (lista blanca de la RPC). */
export const CLAVES_RESPUESTA = [...CLAVES_SI_NO, "fecha_apertura", "orden_parcelas"] as const;

export type ClaveSiNo = (typeof CLAVES_SI_NO)[number];
export type ClaveRespuesta = (typeof CLAVES_RESPUESTA)[number];

/** Valor de cada clave. `null` borra la respuesta y vuelve el valor por defecto. */
export interface ValoresRespuesta {
  aplica_3_2: boolean;
  aplica_3_3: boolean;
  aplica_3_4: boolean;
  aplica_3_5: boolean;
  periodo_confirmado: boolean;
  hubo_analisis: boolean;
  /**
   * D17: la eficacia «Buena» por defecto, confirmada en bloque por el titular. Vale
   * para los partes guardados ANTES de la confirmación (la hora `en` que pone la RPC,
   * M1); los de después se vuelven a preguntar.
   */
  eficacia_confirmada: boolean;
  /** «aaaa-mm-dd», como mucho el 31-12 del año del cuaderno. */
  fecha_apertura: string;
  /** Nº de orden FORZADO por parcela: clave de parcela (`parcelas[].clave` del build) → 1…9999, sin repetir. */
  orden_parcelas: Record<string, number>;
}

/* ───────────── EL DOCUMENTO ES DE LA PERSONA (CU9, decisión de Pablo del 28-09-2026) ─────────────
 *
 * «El cuaderno de campo es SU documento. No podemos dar por exactos los partes. Si en
 * el chat dicen "modifícame esto", "ponme más/menos cantidad de esto", "quita este
 * producto", se hace SIN PREGUNTAR. Los registros (partes) se quedan como están; el
 * documento lo hacen como quieran.»
 *
 * Por eso el borrador guarda, además de las decisiones de arriba:
 *   · EDICIONES de una línea (`override_line`): lo que la persona quiere que diga el
 *     documento en un campo (dosis, cantidad total, producto y nº, plaga, superficie,
 *     fecha, eficacia, aplicador, equipo, observaciones). La línea se identifica con la
 *     MISMA clave que `exclude_line` (el id de `spray_log_entries`) o con el id de una
 *     línea añadida. `valor: null` quita la edición y vuelve lo que dicen los partes;
 *   · LÍNEAS AÑADIDAS (`add_line`): una aplicación que no está en ningún parte (fecha,
 *     nave(s), producto y nº, dosis, plaga…), con un id que pone quien la propone; y
 *     `remove_line` para quitarla.
 * Nada de esto toca `spray_logs`, `spray_log_entries` ni el inventario: vive en
 * `cuaderno_drafts.line_edits` y `cuaderno_drafts.added_lines` (migración
 * 20260928090000), se escribe por la misma RPC y deja el mismo evento de auditoría.
 * Y `exclude_line` ya no rechaza un fitosanitario (D16 queda SUPERSEDIDA): la línea sale
 * del DOCUMENTO; el parte sigue igual.
 */

/** Los campos de una línea que la persona puede cambiar en el documento. La MISMA lista que la RPC. */
export const CAMPOS_EDICION = [
  "dosis",
  "cantidad",
  "producto",
  "plaga",
  "superficie",
  "fecha",
  "eficacia",
  "aplicador",
  "equipo",
  "observaciones",
] as const;
export type CampoEdicion = (typeof CAMPOS_EDICION)[number];
export const esCampoEdicion = (c: unknown): c is CampoEdicion => typeof c === "string" && (CAMPOS_EDICION as readonly string[]).includes(c);

/** Los valores de la eficacia: los MISMOS que `entrada.ts#EFICACIAS` (cuadernoLibre.test.ts lo comprueba). Aquí sin importarlos: el banco del asistente carga este fichero con node, sin resolver imports. */
const EFICACIAS_EDICION: readonly Eficacia[] = ["buena", "regular", "mala"];

/** Unidades de la dosis (las del 3.1) y de la cantidad total. */
export const UNIDADES_DOSIS = ["l/ha", "kg/ha"] as const;
export const UNIDADES_CANTIDAD = ["l", "kg"] as const;
export type UnidadDosis = (typeof UNIDADES_DOSIS)[number];
export type UnidadCantidad = (typeof UNIDADES_CANTIDAD)[number];

/** Topes de lo que se escribe (los mismos que la RPC). */
export { EFICACIAS_EDICION };
export const EDICION_MAX = { dosis: 100000, cantidad: 1000000, superficie: 100000, texto: 200, observaciones: 500, numero: 40, naves: 50 } as const;

/** El valor de cada campo editable. */
export interface ValoresEdicion {
  /** Dosis por hectárea, como la dice la persona: se imprime tal cual. */
  dosis: { valor: number; unidad: UnidadDosis };
  /** Cantidad TOTAL aplicada: la dosis del documento sale de ella entre la superficie. */
  cantidad: { valor: number; unidad: UnidadCantidad };
  /** Nombre comercial y nº de registro (null = sin nº). */
  producto: { nombre: string; numero: string | null };
  plaga: string;
  /** Superficie tratada, en hectáreas. */
  superficie: number;
  /** «aaaa-mm-dd», dentro del año del cuaderno. */
  fecha: string;
  eficacia: Eficacia;
  /** Nombre de la persona o empresa que aplicó (el 1.2 la numera). */
  aplicador: string;
  /** Descripción de la máquina (el 1.3 la numera). */
  equipo: string;
  observaciones: string;
}
export type EdicionLinea = { [K in CampoEdicion]?: ValoresEdicion[K] };

/** Una aplicación que la persona añade al documento: la fecha, las naves y el producto, y los campos que quiera. */
export type LineaNueva = EdicionLinea & { fecha: string; naves: string[]; producto: ValoresEdicion["producto"] };

/** Marca de la tarjeta que aplicó la operación (para el evento de auditoría y para «Deshacer»). */
interface MarcaCambio {
  /** `change_id` de la tarjeta: queda en el evento de auditoría. */
  cambio?: string;
  /** true = esta operación DESHACE la tarjeta `cambio`. */
  deshacer?: boolean;
}

/** Las operaciones de la lista cerrada. Cualquier otra cosa, la RPC la rechaza. */
export type CuadernoOp = (
  | { [K in ClaveRespuesta]: { op: "set_answer"; clave: K; valor: ValoresRespuesta[K] | null } }[ClaveRespuesta]
  | { op: "exclude_line"; spray_log_entry_id: string; motivo: string }
  | { op: "include_line"; spray_log_entry_id: string }
  | { [K in CampoEdicion]: { op: "override_line"; linea: string; campo: K; valor: ValoresEdicion[K] | null } }[CampoEdicion]
  | { op: "add_line"; linea: string; datos: LineaNueva }
  | { op: "remove_line"; linea: string }
) &
  MarcaCambio;

/** Las operaciones que cambian lo que dice el DOCUMENTO sobre una línea (CU9): se aplican sin preguntar. */
export const OPS_DOCUMENTO = ["exclude_line", "include_line", "override_line", "add_line", "remove_line"] as const;

/** Los nombres de las operaciones del borrador (la RPC no acepta otros). */
export const OPS_BORRADOR = ["set_answer", "exclude_line", "include_line", "override_line", "add_line", "remove_line"] as const;
export type OpBorrador = (typeof OPS_BORRADOR)[number];
export const esOpBorrador = (op: unknown): op is OpBorrador => typeof op === "string" && (OPS_BORRADOR as readonly string[]).includes(op);

/** Operaciones por llamada: la RPC acepta de 1 a 500. */
export const MAX_OPS_BORRADOR = 500;
/** Largo del motivo de una exclusión: la RPC pide de 1 a 300 caracteres (sin los espacios de los bordes). */
export const MOTIVO_MAX = 300;

/** Motivos con los que la RPC rechaza una operación (en `details`, como JSON). */
export type MotivoRechazo =
  | "op_desconocida"
  | "clave_no_permitida"
  | "campo_no_permitido"
  | "valor_invalido"
  | "motivo_obligatorio"
  | "linea_invalida"
  | "fuera_de_alcance"
  | "otro_cuaderno"
  | "fitosanitario_registrado"
  | "fitosanitario_por_categoria"
  // A2 (lista blanca): el nombre casa con el Registro MAPA, o no es ni abono ni auxiliar.
  | "fitosanitario_por_nombre"
  | "no_es_abono_ni_auxiliar";

/**
 * Las categorías de producto que SON fitosanitarias (D16 endurecida en CQ4): una
 * línea de una de ellas NO sale del cuaderno aunque no tenga nº de registro,
 * porque un fitosanitario sin nº es un fitosanitario al que le falta el nº (se
 * queda, con el nº como hueco, y se ofrece corregir el parte). Las tres del alta
 * de producto (`inventory.category`) más los alias que aún pueden venir en la
 * copia de la línea (`normalizeCategory`: insecticida, acaricida, nematicida).
 * «biological» no: ahí van los auxiliares (macroorganismos), que sí pueden salir;
 * un biológico con nº ya lo protege el nº.
 *
 * La MISMA lista que `cuaderno_linea_se_queda` en la migración 20260926140000
 * (cuadernoBorrador.test.ts la compara con el SQL). Es UNO de los indicios de
 * `motivoSeQueda` (abajo): desde A2 lo que decide es la lista blanca.
 */
export const CATEGORIAS_FITOSANITARIAS = ["pesticide", "fungicide", "herbicide", "insecticide", "acaricide", "nematicide"] as const;

/** ¿Es fitosanitaria esta categoría? Sin distinguir mayúsculas ni espacios de los bordes, como el SQL. */
export function esCategoriaFitosanitaria(categoria: string | null | undefined): boolean {
  const c = String(categoria ?? "").trim().toLowerCase();
  return !!c && (CATEGORIAS_FITOSANITARIAS as readonly string[]).includes(c);
}

/* ───────────── D16 como LISTA BLANCA (A2 de la revisión de la ola 3) ─────────────
 *
 * SUPERSEDIDA el 28-09-2026 por decisión de Pablo (CU9, «el documento es de la
 * persona»): la RPC, el motor y la edge YA NO impiden sacar un fitosanitario del
 * cuaderno. `motivoSeQueda` sigue aquí como INDICIO: la guarda de salida lo usa para
 * AVISAR («has quitado un fitosanitario») sin bloquear, y la edge para decir qué es
 * cada línea. El texto de abajo es el de la regla vieja, que se conserva.
 *
 * «Un fitosanitario nunca sale del cuaderno» era una LISTA NEGRA (se quedaba lo que
 * tenía nº o categoría fitosanitaria) y dejaba salir fitosanitarios de verdad: en
 * 2026, 65 líneas de Nijasol y 73 de la demo (COSTAR, FLASH UM, AZU'S, MULIGAN,
 * Tutavir, Bacillus thuringiensis…) llevan categoría null, «other», «biological» o
 * «chemical» (el producto libre copia `product_type` en `category_snapshot`).
 *
 * Ahora SOLO puede salir lo que el código SABE que no es un fitosanitario:
 *   · un ABONO: la categoría de la línea (o, si la línea no tiene, la del producto
 *     hoy) es fertilizer / biostimulant / abono (`esAbono`), o
 *   · un MACROORGANISMO (auxiliar): el nombre lleva uno de `MACROORGANISMOS`
 *     (Nesidiocoris, Swirski…; es como se distinguen hoy: por el nombre);
 * y ADEMÁS no tiene ningún indicio de fitosanitario:
 *   · nº de registro válido (el resolvedor), ni
 *   · categoría fitosanitaria en la línea o en el producto, ni
 *   · un nombre que casa con el Registro MAPA (`casaConRegistroPorNombre`): un
 *     «biológico» o un «abono» que se llama como un producto registrado se queda.
 * Todo lo demás se queda en el 3.1, con el nº como hueco si le falta.
 *
 * La MISMA regla en los tres sitios: la RPC (`cuaderno_linea_se_queda` en la
 * migración 20260926140000; cuadernoBorrador.test.ts compara las listas), el motor
 * (`rows31.clasificarLinea`) y la herramienta de la edge (tools/cuaderno.ts), y la
 * vigila la guarda de salida (regla 4).
 */

/** ¿Es un abono por su categoría? La MISMA expresión que la RPC (`~* '^(fertili[sz]er|biostimulant|abono)'`). */
export const ABONO_RE = /^(fertili[sz]er|biostimulant|abono)/i;
export const esAbono = (categoria: string | null | undefined): boolean => ABONO_RE.test(String(categoria ?? "").trim());

/** ¿Es un auxiliar (macroorganismo) por su nombre? Contiene uno de `MACROORGANISMOS`, sin distinguir mayúsculas. */
export const esMacroorganismo = (nombre: string | null | undefined): boolean => {
  const n = String(nombre ?? "").toLowerCase();
  return !!n && MACROORGANISMOS.some((m) => n.includes(m));
};

/**
 * Los nombres del Registro MAPA, normalizados UNA vez por cuaderno
 * (`normalizeProductName`, el espejo de `normalize_product_name` que da
 * `nombre_norm`): los nombres enteros y sus principios por palabras.
 */
export interface IndiceNombresRegistro {
  nombres: Set<string>;
  principios: Set<string>;
}

export function indiceNombresRegistro(catalogo: ReadonlyArray<{ nombre: string }> | null | undefined): IndiceNombresRegistro | null {
  if (!catalogo || catalogo.length === 0) return null;
  const nombres = new Set<string>();
  const principios = new Set<string>();
  for (const it of catalogo) {
    const r = normalizeProductName(it?.nombre);
    if (!r) continue;
    nombres.add(r);
    const w = r.split(" ");
    for (let i = 1; i < w.length; i++) principios.add(w.slice(0, i).join(" "));
  }
  return { nombres, principios };
}

/**
 * ¿El nombre de la línea casa con un producto del Registro MAPA? Casa si, ya
 * normalizados, son iguales o uno es el otro más palabras detrás («SPINTOR 480 SC
 * 1L» → SPINTOR 480 SC; «COSTAR ECO SG» → COSTAR; «FLASH» → FLASH UM). La MISMA
 * regla que la RPC (con `nombre_norm`). Sin catálogo (o con el MAPA apagado, D26),
 * no casa: queda la RPC, que lo mira contra la copia del registro.
 */
export function casaConRegistroPorNombre(nombre: string | null | undefined, indice: IndiceNombresRegistro | null): boolean {
  if (!indice) return false;
  const n = normalizeProductName(nombre);
  if (!n) return false;
  if (indice.nombres.has(n) || indice.principios.has(n)) return true;
  const w = n.split(" ");
  for (let i = 1; i < w.length; i++) if (indice.nombres.has(w.slice(0, i).join(" "))) return true;
  return false;
}

/** Por qué una línea se QUEDA en el cuaderno aunque se pida sacarla (o null si puede salir). */
export type MotivoSeQueda =
  | "fitosanitario_registrado"
  | "fitosanitario_por_categoria"
  | "fitosanitario_por_nombre"
  | "no_es_abono_ni_auxiliar";

export interface LineaParaSalir {
  /** El resolvedor dio un nº válido (el de la línea o el del producto hoy). */
  registroValido: boolean;
  /** `category_snapshot` de la línea. */
  categoriaLinea: string | null | undefined;
  /** `inventory.category` del producto hoy (`product_id ?? inventory_item_id`). */
  categoriaProducto: string | null | undefined;
  nombre: string | null | undefined;
  /** `casaConRegistroPorNombre` (false sin catálogo). */
  casaPorNombre: boolean;
}

/** D16 en lista blanca: null = puede salir; si no, por qué se queda. Mismo orden de motivos que la RPC. */
export function motivoSeQueda(l: LineaParaSalir): MotivoSeQueda | null {
  if (l.registroValido) return "fitosanitario_registrado";
  if (esCategoriaFitosanitaria(l.categoriaLinea) || esCategoriaFitosanitaria(l.categoriaProducto)) return "fitosanitario_por_categoria";
  if (l.casaPorNombre) return "fitosanitario_por_nombre";
  const categoria = String(l.categoriaLinea ?? "").trim() || String(l.categoriaProducto ?? "").trim();
  if (esAbono(categoria) || esMacroorganismo(l.nombre)) return null;
  return "no_es_abono_ni_auxiliar";
}

export const lineaPuedeSalir = (l: LineaParaSalir): boolean => motivoSeQueda(l) === null;

/** Lo que devuelve la RPC cuando aplica. */
export interface ResultadoCambio {
  applied: number;
  changed: number;
  version: number;
  draft_id: string;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const FECHA_ISO = /^\d{4}-\d{2}-\d{2}$/;
const esClaveSiNo = (c: unknown): c is ClaveSiNo => typeof c === "string" && (CLAVES_SI_NO as readonly string[]).includes(c);
export const esClaveRespuesta = (c: unknown): c is ClaveRespuesta => typeof c === "string" && (CLAVES_RESPUESTA as readonly string[]).includes(c);

/** «aaaa-mm-dd» que existe (el 30-02 no), como el `::date` de la RPC. */
function fechaReal(v: string): boolean {
  if (!FECHA_ISO.test(v)) return false;
  const d = new Date(`${v}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v;
}

const esNumeroEn = (v: unknown, max: number): v is number => typeof v === "number" && Number.isFinite(v) && v > 0 && v <= max;
const textoEn = (v: unknown, max: number): v is string => typeof v === "string" && v.trim().length > 0 && v.trim().length <= max && v.trim() === v;
const soloClaves = (o: Record<string, unknown>, claves: string[]) => Object.keys(o).every((k) => claves.includes(k));

/**
 * ¿Vale este valor para ese campo de una línea del documento (CU9)? La MISMA regla
 * que la RPC (`cuaderno_valor_edicion_valido`): números positivos con tope, unidades
 * de la lista, textos sin espacios en los bordes y con tope, fecha real dentro del
 * año del cuaderno (con `year`). No mira si un nº de registro existe: eso lo AVISA
 * la guarda de salida, no lo bloquea nadie (lo decide la persona).
 */
export function validarValorEdicion(campo: CampoEdicion, v: unknown, year?: number): boolean {
  switch (campo) {
    case "dosis":
    case "cantidad": {
      if (!v || typeof v !== "object" || Array.isArray(v)) return false;
      const o = v as Record<string, unknown>;
      const unidades: readonly string[] = campo === "dosis" ? UNIDADES_DOSIS : UNIDADES_CANTIDAD;
      return soloClaves(o, ["valor", "unidad"]) && esNumeroEn(o.valor, EDICION_MAX[campo]) && typeof o.unidad === "string" && unidades.includes(o.unidad);
    }
    case "producto": {
      if (!v || typeof v !== "object" || Array.isArray(v)) return false;
      const o = v as Record<string, unknown>;
      return soloClaves(o, ["nombre", "numero"]) && textoEn(o.nombre, EDICION_MAX.texto) && (o.numero == null || textoEn(o.numero, EDICION_MAX.numero));
    }
    case "superficie":
      return esNumeroEn(v, EDICION_MAX.superficie);
    case "fecha":
      if (typeof v !== "string" || !fechaReal(v)) return false;
      return year == null || (v >= `${year}-01-01` && v <= `${year}-12-31`);
    case "eficacia":
      return typeof v === "string" && (EFICACIAS_EDICION as readonly string[]).includes(v);
    case "observaciones":
      return textoEn(v, EDICION_MAX.observaciones);
    case "plaga":
    case "aplicador":
    case "equipo":
      return textoEn(v, EDICION_MAX.texto);
  }
}

/** ¿Valen los datos de una línea añadida? Fecha, naves (1–50 ids sin repetir) y producto obligatorios; lo demás, como una edición. */
export function validarLineaNueva(v: unknown, year?: number): boolean {
  if (!v || typeof v !== "object" || Array.isArray(v)) return false;
  const o = v as Record<string, unknown>;
  if (!soloClaves(o, ["naves", ...CAMPOS_EDICION])) return false;
  if (!validarValorEdicion("fecha", o.fecha, year) || !validarValorEdicion("producto", o.producto)) return false;
  const naves = o.naves;
  if (!Array.isArray(naves) || naves.length === 0 || naves.length > EDICION_MAX.naves) return false;
  if (!naves.every((n) => typeof n === "string" && UUID.test(n)) || new Set(naves.map((n) => String(n).toLowerCase())).size !== naves.length) return false;
  for (const c of CAMPOS_EDICION) {
    if (c === "fecha" || c === "producto") continue;
    if (o[c] != null && !validarValorEdicion(c, o[c], year)) return false;
  }
  return true;
}

/** La marca de la tarjeta (opcional): `cambio` un uuid y `deshacer` un booleano. */
function marcaValida(o: Record<string, unknown>): boolean {
  if (o.cambio !== undefined && (typeof o.cambio !== "string" || !UUID.test(o.cambio))) return false;
  if (o.deshacer !== undefined && typeof o.deshacer !== "boolean") return false;
  return true;
}

/**
 * La PRIMERA pasada de `apply_cuaderno_change` para UNA operación, sin mirar la
 * base: el motivo con el que la RPC la rechazaría, o null. Lo que necesita la
 * base (alcance, año y titular de la línea, fitosanitario) lo sigue mirando la
 * RPC; aquí se para lo que ni siquiera tiene forma, antes de enseñar una
 * tarjeta o de llamar. Con `year`, la fecha de apertura va como en la RPC: del
 * 1-1 del año anterior al 31-12 del año del cuaderno.
 *
 * Un poco más estricta que la RPC en una cosa: `valor` tiene que venir (null
 * borra la respuesta); la RPC toma la clave ausente como null.
 */
export function validarOpBorrador(raw: unknown, year?: number): MotivoRechazo | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return "op_desconocida";
  const o = raw as Record<string, unknown>;
  if (esOpBorrador(o.op) && !marcaValida(o)) return "valor_invalido";
  if (o.op === "override_line") {
    if (typeof o.linea !== "string" || !UUID.test(o.linea)) return "linea_invalida";
    if (!esCampoEdicion(o.campo)) return "campo_no_permitido";
    if (!("valor" in o) || o.valor === undefined) return "valor_invalido";
    return o.valor === null || validarValorEdicion(o.campo, o.valor, year) ? null : "valor_invalido";
  }
  if (o.op === "add_line") {
    if (typeof o.linea !== "string" || !UUID.test(o.linea)) return "linea_invalida";
    return validarLineaNueva(o.datos, year) ? null : "valor_invalido";
  }
  if (o.op === "remove_line") {
    return typeof o.linea === "string" && UUID.test(o.linea) ? null : "linea_invalida";
  }
  if (o.op === "set_answer") {
    if (!esClaveRespuesta(o.clave)) return "clave_no_permitida";
    if (!("valor" in o) || o.valor === undefined) return "valor_invalido";
    const v = o.valor;
    if (v === null) return null;
    if (esClaveSiNo(o.clave)) return typeof v === "boolean" ? null : "valor_invalido";
    if (o.clave === "fecha_apertura") {
      if (typeof v !== "string" || !fechaReal(v)) return "valor_invalido";
      if (year != null && (v > `${year}-12-31` || v < `${year - 1}-01-01`)) return "valor_invalido";
      return null;
    }
    // orden_parcelas: { clave de parcela: 1…9999 }, sin repetir número.
    if (typeof v !== "object" || Array.isArray(v)) return "valor_invalido";
    const pares = Object.entries(v as Record<string, unknown>);
    if (pares.length > 500) return "valor_invalido";
    const numeros = new Set<number>();
    for (const [clave, n] of pares) {
      if (!clave || clave.length > 300) return "valor_invalido";
      if (typeof n !== "number" || !Number.isInteger(n) || n < 1 || n > 9999 || numeros.has(n)) return "valor_invalido";
      numeros.add(n);
    }
    return null;
  }
  if (o.op === "exclude_line" || o.op === "include_line") {
    if (typeof o.spray_log_entry_id !== "string" || !UUID.test(o.spray_log_entry_id)) return "linea_invalida";
    if (o.op === "exclude_line") {
      const motivo = typeof o.motivo === "string" ? o.motivo.trim() : "";
      if (!motivo || motivo.length > MOTIVO_MAX) return "motivo_obligatorio";
    }
    return null;
  }
  return "op_desconocida";
}

/**
 * El `p_expected_count` de la RPC: operaciones DISTINTAS, una por clave de
 * `set_answer` y una por línea. Dos operaciones sobre lo mismo cuentan una.
 */
export function recuentoDeOps(ops: CuadernoOp[]): number {
  const vistos = new Set<string>();
  for (const o of ops) {
    if (o.op === "set_answer") vistos.add(`a:${o.clave}`);
    else if (o.op === "override_line") vistos.add(`e:${String(o.linea).toLowerCase()}:${o.campo}`);
    else if (o.op === "add_line" || o.op === "remove_line") vistos.add(`n:${String(o.linea).toLowerCase()}`);
    else vistos.add(`l:${String(o.spray_log_entry_id).toLowerCase()}`);
  }
  return vistos.size;
}

/** Una fila de `cuaderno_drafts`, tal y como llega de la base. */
export interface BorradorCuaderno {
  id: string;
  company_id: string;
  titular_location_id: string | null;
  year: number;
  answers: unknown;
  line_overrides: unknown;
  /** CU9: ediciones del documento por línea (`{ linea: { campo: { v, por, en } } }`). Sin la migración, no viene. */
  line_edits?: unknown;
  /** CU9: líneas añadidas al documento (`{ id: { v: LineaNueva, por, en } }`). */
  added_lines?: unknown;
  version: number;
  updated_at?: string | null;
}

/** Lo que el borrador decide, ya leído y validado. */
export interface DecisionesBorrador {
  /** Para el motor (`EntradaCuaderno.decisiones`). */
  decisiones: DecisionesCuaderno;
  /** Líneas fuera, con su motivo. */
  excluidas: Record<string, string>;
  fechaApertura: string | null;
  ordenForzado: Record<string, number>;
  eficaciaConfirmada: boolean;
  /**
   * Hasta cuándo vale la confirmación de la eficacia (M1): el `en` que puso la RPC al
   * guardarla (hora del servidor). Solo cuenta para los partes guardados ANTES; los
   * que se registren después vuelven a preguntarse. null = sin confirmar (o sin `en`).
   */
  eficaciaConfirmadaHasta: string | null;
  /** CU9: lo que la persona cambió de cada línea en el documento (solo lo que vale). */
  ediciones: Record<string, EdicionLinea>;
  /** CU9: aplicaciones añadidas al documento, por id. */
  lineasNuevas: Record<string, LineaNueva>;
  version: number;
}

const objeto = (v: unknown): Record<string, unknown> =>
  v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {};

/** `{ v, por, en }` → v. Tolera un valor suelto (por si alguien lo escribió a mano). */
function valorDe(raw: unknown): unknown {
  const o = objeto(raw);
  return "v" in o ? o.v : raw;
}

/**
 * Del jsonb del borrador a las decisiones. Lo que no cuadra con la lista blanca
 * se ignora (la RPC no lo deja escribir, pero una fila vieja o tocada a mano no
 * puede romper el cuaderno).
 */
export function leerBorrador(
  draft: (Pick<BorradorCuaderno, "answers" | "line_overrides" | "version"> & Partial<Pick<BorradorCuaderno, "line_edits" | "added_lines">>) | null | undefined,
): DecisionesBorrador {
  const answers = objeto(draft?.answers);
  const si = (k: ClaveSiNo): boolean | undefined => {
    const v = valorDe(answers[k]);
    return typeof v === "boolean" ? v : undefined;
  };

  const aplica: DecisionesCuaderno["aplica"] = {};
  for (const [k, sec] of [["aplica_3_2", "3.2"], ["aplica_3_3", "3.3"], ["aplica_3_4", "3.4"], ["aplica_3_5", "3.5"]] as const) {
    const v = si(k);
    if (v !== undefined) aplica[sec] = v;
  }

  const fechaRaw = valorDe(answers.fecha_apertura);
  const fechaApertura = typeof fechaRaw === "string" && FECHA_ISO.test(fechaRaw) ? fechaRaw : null;

  const ordenForzado: Record<string, number> = {};
  const usados = new Set<number>();
  for (const [clave, n] of Object.entries(objeto(valorDe(answers.orden_parcelas)))) {
    if (typeof n === "number" && Number.isInteger(n) && n >= 1 && n <= 9999 && !usados.has(n) && clave) {
      ordenForzado[clave] = n;
      usados.add(n);
    }
  }

  const excluidas: Record<string, string> = {};
  for (const [linea, raw] of Object.entries(objeto(draft?.line_overrides))) {
    const motivo = objeto(raw).motivo;
    excluidas[linea] = typeof motivo === "string" && motivo.trim() ? motivo.trim() : "—";
  }

  // CU9: las ediciones y las líneas añadidas; lo que no vale (fila vieja o tocada a mano) se ignora.
  const ediciones: Record<string, EdicionLinea> = {};
  for (const [linea, raw] of Object.entries(objeto(draft?.line_edits))) {
    if (!UUID.test(linea)) continue;
    const ed: EdicionLinea = {};
    for (const [campo, celda] of Object.entries(objeto(raw))) {
      const v = valorDe(celda);
      if (esCampoEdicion(campo) && v != null && validarValorEdicion(campo, v)) (ed as Record<string, unknown>)[campo] = v;
    }
    if (Object.keys(ed).length) ediciones[linea.toLowerCase()] = ed;
  }
  const lineasNuevas: Record<string, LineaNueva> = {};
  for (const [id, raw] of Object.entries(objeto(draft?.added_lines))) {
    const v = valorDe(raw);
    if (UUID.test(id) && validarLineaNueva(v)) lineasNuevas[id.toLowerCase()] = v as LineaNueva;
  }

  const hubo = si("hubo_analisis");
  // M1: la fecha de corte es el `en` de la RPC; sin él (fila tocada a mano), no se confirma nada.
  const enEficacia = objeto(answers.eficacia_confirmada).en;
  const eficaciaConfirmadaHasta =
    si("eficacia_confirmada") === true && typeof enEficacia === "string" && !Number.isNaN(Date.parse(enEficacia)) ? enEficacia : null;
  return {
    decisiones: {
      excluidas,
      aplica,
      periodoConfirmado: si("periodo_confirmado") === true,
      huboAnalisis: hubo === undefined ? null : hubo,
      eficaciaConfirmadaHasta,
      ediciones,
    },
    excluidas,
    fechaApertura,
    ordenForzado,
    eficaciaConfirmada: eficaciaConfirmadaHasta !== null,
    eficaciaConfirmadaHasta,
    ediciones,
    lineasNuevas,
    version: Number(draft?.version ?? 0) || 0,
  };
}

/** El mensaje de error de la RPC y su detalle, para enseñarlo sin inventar. */
export interface ErrorCambio {
  codigo: string;
  rechazos: { i?: number; motivo: MotivoRechazo | string; linea?: string; clave?: string }[];
  recuento: { esperado: number | null; aplicables: number; no_encontradas: string[] } | null;
}

/** Lee `{ message, details }` de PostgREST. Sin JSON en `details`, sin rechazos. */
export function leerErrorCambio(error: { message?: string; details?: string | null } | null | undefined): ErrorCambio {
  const codigo = String(error?.message ?? "").trim() || "ERROR";
  let rechazos: ErrorCambio["rechazos"] = [];
  let recuento: ErrorCambio["recuento"] = null;
  try {
    const d = error?.details ? JSON.parse(error.details) : null;
    if (Array.isArray(d)) rechazos = d;
    else if (d && typeof d === "object" && "aplicables" in d) recuento = d as ErrorCambio["recuento"];
  } catch {
    /* sin detalle legible */
  }
  return { codigo, rechazos, recuento };
}
